import jsonpath from 'jsonpath';
import { BusinessDocType, ControlType } from '../../legacy/transcepta-types';
import DocumentViewContainer from '../../legacy/transcepta-types/types/DocumentViewContainer';
import { InvoiceLineItem } from '../../services/backendServices/ViewModels/DocumentViewContainer';
import { cleanForbiddenChars, DEFAULT_FORBIDDEN_CHARS } from '../inputValidation';
import { removeIdAttributes } from '../xmlIdAttributeRemover';

export { removeEmptyEntitiesFromDocument } from './removeEmptyEntitiesFromDocument';
export { DEFAULT_FORBIDDEN_CHARS };
export { AI_WEB_ENTRY_MISC_INFO_KEYWORD, buildAiWebEntryMiscInfo, parseAiWebEntryMiscInfo } from './aiWebEntryMiscInfo';

// Substitutions to apply before removing forbidden chars (for document cleanup only)
const FORBIDDEN_CHAR_SUBSTITUTIONS: Record<string, string> = {
    '＆': '&', // Full-width ampersand -> regular ampersand
};

function cleanStringForDocument(value: string, forbiddenChars: string[]): string {
    let result = value;

    // First, apply substitutions
    for (const [badChar, goodChar] of Object.entries(FORBIDDEN_CHAR_SUBSTITUTIONS)) {
        result = result.split(badChar).join(goodChar);
    }

    // Then, remove any remaining forbidden chars
    result = cleanForbiddenChars(result, forbiddenChars).cleanedValue;

    return result;
}

/**
 * Recursively cleans forbidden characters from all string values in a document object.
 * Substitutes known bad characters with good equivalents, then removes any remaining forbidden chars.
 */
export function removeForbiddenCharsFromDocument(doc: any, forbiddenChars: string[] = DEFAULT_FORBIDDEN_CHARS): any {
    if (doc === null || doc === undefined) {
        return doc;
    }

    if (typeof doc === 'string') {
        return cleanStringForDocument(doc, forbiddenChars);
    }

    if (Array.isArray(doc)) {
        return doc.map((item) => removeForbiddenCharsFromDocument(item, forbiddenChars));
    }

    if (typeof doc === 'object') {
        const result: Record<string, any> = {};
        for (const key of Object.keys(doc)) {
            result[key] = removeForbiddenCharsFromDocument(doc[key], forbiddenChars);
        }
        return result;
    }

    return doc;
}

const documentLineHasGLCodingInfo = (lineItem: InvoiceLineItem) =>
    lineItem.InvoiceLineItemCompanyGLAccounts.some((glAccount) => {
        const coding = glAccount.Coding.Coding;

        const values = Object.values(coding).map(({ value }: any) => value);

        return !!values.join('');
    });

const documentLineIsBlank = (bodyLayout: any, bodySectionNode: any) => {
    const excludedFieldDisplayHints = ['LineNumber'];
    const excludedFieldNames = ['LineNum'];
    const numericFields = [
        ControlType.Money,
        ControlType.Quantity,
        ControlType.UnitPrice,
        ControlType.Tax,
        ControlType.UnitAmountAndAmount,
    ];

    const excludedFieldTypes = [ControlType.GLCodingDotNotation, ControlType.GLCodingInline];

    for (let cellIndex = 0; cellIndex < bodyLayout.length; cellIndex++) {
        const cellLayout = bodyLayout[cellIndex];
        for (let fieldIndex = 0; fieldIndex < cellLayout.Fields.length; fieldIndex++) {
            const fieldLayout = cellLayout.Fields[fieldIndex];

            if (
                !excludedFieldDisplayHints.includes(fieldLayout.DisplayHints) &&
                !excludedFieldTypes.includes(fieldLayout.ControlType) &&
                !excludedFieldNames.includes(fieldLayout.FieldName)
            ) {
                const cellAndFieldJSONPath =
                    cellLayout.CellJSONPath && cellLayout.CellJSONPath !== ''
                        ? `$..${cellLayout.CellJSONPath}.${fieldLayout.JSONPath}`
                        : `$..${fieldLayout.JSONPath}`;
                let fieldValue = null;

                try {
                    fieldValue = jsonpath.query(bodySectionNode, cellAndFieldJSONPath)[0];
                } catch (e) {
                    console.error('Invalid jsonpath: ', cellAndFieldJSONPath);
                    break;
                }

                if (numericFields.includes(fieldLayout.ControlType)) {
                    if (fieldValue && Number(fieldValue) !== 0) {
                        return false;
                    }
                } else if (fieldValue && fieldValue !== '') {
                    return false;
                }
            }
        }
    }

    if (documentLineHasGLCodingInfo(bodySectionNode)) {
        return false;
    }

    return true;
};

export const removeBlankDocumentLines = (
    layout: any,
    document: DocumentViewContainer,
    businessDocType: BusinessDocType
) => {
    if (businessDocType === BusinessDocType.SIMDocument) {
        return;
    }

    const isInvoice = businessDocType === BusinessDocType.Invoice;

    const bodyLayout = layout.Large.Body;
    const bodyJsonPath = `$${bodyLayout[0].SectionJSONPath}`;

    const uniqueSectionNodes = jsonpath.nodes(document, bodyJsonPath)[0].value;
    let remainingLineCount = uniqueSectionNodes.length;

    for (
        let sectionNodeIndex = uniqueSectionNodes.length - 1;
        sectionNodeIndex >= 0 && remainingLineCount > 1;
        sectionNodeIndex--
    ) {
        const sectionNode = uniqueSectionNodes[sectionNodeIndex];
        if (documentLineIsBlank(bodyLayout, sectionNode)) {
            uniqueSectionNodes.splice(sectionNodeIndex, 1);
            // Generic doc types do not use an array for their lines when there is only one line.
            // Therefore if this is not an invoice, we must convert from an array to a single object if there will be only one line remaining.
            if (!isInvoice && uniqueSectionNodes.length === 1) {
                jsonpath.value(document, bodyJsonPath, uniqueSectionNodes[0]);
            }
            remainingLineCount -= 1;
        }
    }
};

export const formatCustomData = (existingCustomData: any, reason: string | undefined) => {
    let customData: Record<string, any> = {
        CustomData: {
            ...(existingCustomData?.CustomData ?? {}),
            Workflow: {
                WorkflowApprovalComment: reason ?? '',
            },
        },
    };

    customData = removeIdAttributes(customData);

    return customData;
};
